import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert, CmdSubpage, CmdSubpageButton } from '@commander-services/gui-components';
import localforage from 'localforage';
import { useQueries } from '@tanstack/react-query';
import Driving from './Driving';
import Stopped from './Stopped';
import { IWidgetGraph } from '../../../OnlinePanel/OnlinePanelState';
import { IVehicleData } from '../../../Vehicles/types';
import SelectRidesModal from '../../SelectRidesModal';
import { URL_ECO_DRIVE, URL_GRAPHS } from '../../../../router/constants';
import {
  getVehicleName,
  getIsCompressed,
  getData,
  getDataByRideId,
  IGraphResponseData,
  getRideStartDate,
  getRideEndDate,
  getSubpageVehicleName,
  getDateFrom,
  getDateTo,
  getPartData,
  getPartDataByRideId,
  getRideStartDateAndAddress,
  getRideEndDateAndAddress,
} from '../../GraphService';
import RideStartAndEnd from '../../RideStartAndEnd';
import PartSelecting from '../../PartSelecting';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import {
  ecoDriveWidgetGraphAtom,
  isGraphOpenAtom,
  lastClickedTimestampAtom,
  widgetGraphSelectedValuesAtom,
} from '../../GraphState';
import { setLoaderAtom } from '../../../../store/recoil/AppState';
import { vehicleDataByIdSelector } from '../../../../store/recoil/vehiclesData';
import { vehicleByIdSelector } from '../../../../store/recoil/vehicles';

interface IEcoDrive {
  vehicleId?: number;
  rideId?: string;
  subpageButtons?: CmdSubpageButton[];
  isGdprAlert?: boolean;
}

export default function EcoDriveOnline(props: IEcoDrive) {
  const [, params] = useRoute(`${URL_GRAPHS}${URL_ECO_DRIVE}/:vehicleId`);
  const { formatMessage: f } = useIntl();
  const vehicleData = useRecoilValue<IVehicleData | null>(
    vehicleDataByIdSelector(props.vehicleId || Number(params?.vehicleId) || 0)
  );
  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const [isOrigCompressed, setIsOrigCompressed] = React.useState(false);
  const [defaultDates, setDefaultDates] = React.useState<Date[] | undefined>(undefined);
  const [startDateAndAddress, setStartDateAndAddress] = React.useState('');
  const [lastDateAndAddress, setLastDateAndAddress] = React.useState('');
  const [endDateAndAddress, setEndDateAndAddress] = React.useState('');
  const [showSelectRidesModal, setShowSelectRidesModal] = React.useState(false);
  const [rideId, setRideId] = React.useState<string | null>(props.rideId || null);
  const [responseData, setResponseData] = React.useState<IGraphResponseData[]>([]);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [ecoDriveWidgetGraph, setEcoDriveWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    ecoDriveWidgetGraphAtom
  );
  const setLastClickedTimestamp = useSetRecoilState<string>(lastClickedTimestampAtom);
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);
  const [origStatus, setOrigStatus] = React.useState('');
  const vehicle = useRecoilValue(
    vehicleByIdSelector(
      props.vehicleId || (params && params.vehicleId ? Number(params.vehicleId) : false)
    )
  );

  React.useEffect(() => {
    if (vehicle) {
      setIsGraphOpen(vehicle.id);
    }
  }, [setIsGraphOpen, vehicle]);

  const { setDocumentTitle } = useDocumentTitle();

  React.useEffect(() => {
    if (vehicle && params?.vehicleId) {
      const documentTitle = `${f({ id: 'rides.ecoScore' }, { valueType: 'L' })} ${getVehicleName(
        vehicle
      )}`;
      setDocumentTitle(documentTitle);
    }
  }, [f, params, setDocumentTitle, vehicle]);

  const resetGraphStates = () => {
    setResponseData([]);
    setLastClickedTimestamp('');
    setStartDateAndAddress('');
    setLastDateAndAddress('');
    setEndDateAndAddress('');
    setIsOrigCompressed(false);
    setOrigStatus(rideId ? 'STOP' : '');
  };

  const handleData = async () => {
    if (vehicle) {
      setLoader(true);
      const response = await getData('ecoScore', String(vehicle.id));
      if (response) {
        setResponseData(response);
        if (params) {
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        }
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        setIsOrigCompressed(getIsCompressed(response));
        if (vehicleData?.status === 'STOP') {
          localforage.setItem('widgetGraph', {
            vehicleId: vehicle.id,
            data: response,
            name: 'ecoScore',
            rideId: null,
          });
        } else {
          localforage.setItem('widgetGraph', null);
        }
        if (ecoDriveWidgetGraph) {
          setEcoDriveWidgetGraph((oldState: any) => {
            return { ...oldState, timestamp: response[0]?.timestamp };
          });
        }
      } else if (!params && ecoDriveWidgetGraph) {
        setEcoDriveWidgetGraph((oldState: any) => {
          return { ...oldState, timestamp: '' };
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const handleDataByRideId = async (id: string) => {
    if (vehicle) {
      setLoader(true);
      const response = await getDataByRideId('ecoScore', String(vehicle.id), id);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        setRideId(id);
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        setIsOrigCompressed(getIsCompressed(response));
        localforage.setItem('widgetGraph', {
          vehicleId: vehicle.id,
          data: response,
          name: 'ecoScore',
          rideId: id,
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const getDataFromLocalforage = async () => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'ecoScore' &&
      !data.rideId
    ) {
      setResponseData(data.data);
      if (params) {
        setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
      }
      setStartDateAndAddress(getRideStartDateAndAddress(data.data));
      setLastDateAndAddress(getRideEndDateAndAddress(data.data));
      setIsOrigCompressed(getIsCompressed(data.data));
    } else {
      handleData();
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['subpageEcoDriveData', rideId, vehicle?.id],
        queryFn: () => handleDataByRideId(rideId || '0'),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(props.subpageButtons && rideId),
      },
      {
        queryKey: ['ecoDriveData', vehicle?.id],
        queryFn: () => getDataFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(params || (!params && ecoDriveWidgetGraph && !ecoDriveWidgetGraph.timestamp)),
      },
    ],
  });

  React.useEffect(() => {
    if (vehicleData?.status && !origStatus) {
      setOrigStatus(vehicleData.status);
    }
  }, [origStatus, vehicleData?.status]);

  const handleModal = () => {
    setShowSelectRidesModal(!showSelectRidesModal);
  };

  const handleRide = (id: string, dates: Date[]) => {
    setWidgetGraphSelectedValues(null);
    handleModal();
    handleDataByRideId(id);
    setDefaultDates(dates);
  };

  const handleEndRideDateAndAddress = (address: string) => {
    setEndDateAndAddress(address);
  };

  const getDataByRideIdFromLocalforage = async (id: string) => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'ecoScore' &&
      data.rideId === id
    ) {
      setResponseData(data.data);
      setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
    } else {
      handleDataByRideId(id);
    }
  };

  const loadPartData = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    if (vehicle) {
      setLoader(true);
      const response = !rideId
        ? await getPartData('ecoScore', String(vehicle.id), [dateFrom, dateTo])
        : await getPartDataByRideId('ecoScore', String(vehicle.id), rideId, [dateFrom, dateTo]);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  const resetPartData = () => {
    if (rideId) {
      getDataByRideIdFromLocalforage(rideId);
    } else {
      getDataFromLocalforage();
    }
  };

  const renderCompressedGraphAlert = () => {
    return getIsCompressed(responseData) ? (
      <div style={{ padding: params ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.alert.title' }),
            message: params
              ? f({ id: 'graph.velocity.alert.message' })
              : f({ id: 'graph.onlinepanel.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  const renderGraphHeader = () => {
    return (
      <div>
        {!props.subpageButtons && (
          <RideStartAndEnd
            endDateAndAddress={endDateAndAddress}
            rideId={rideId}
            origStatus={origStatus}
            handleModal={handleModal}
            startDateAndAddress={startDateAndAddress}
            lastDateAndAddress={lastDateAndAddress}
          />
        )}
        {isOrigCompressed && (origStatus === 'STOP' || rideId) && (
          <PartSelecting
            dateFrom={getDateFrom(responseData)}
            dateTo={getDateTo(responseData)}
            loadPartData={loadPartData}
            loadData={resetPartData}
            data={responseData}
            rideId={rideId}
          />
        )}
        {renderCompressedGraphAlert()}
      </div>
    );
  };

  const handleDataFromNewRide = () => {
    setOrigStatus('DRIVING');
    setRideId('');
    handleData();
  };

  const handleDataAfterFinishedRide = () => {
    setOrigStatus('STOP');
    handleData();
  };

  const renderGraph = () => {
    if (!origStatus) {
      return <div />;
    }
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return origStatus === 'STOP' || rideId ? (
        <Stopped
          responseData={responseData}
          isCompressed={isCompressed}
          rideId={rideId}
          vehicleId={vehicle.id}
          loadData={handleDataFromNewRide}
          isDetail={!!(params || props.subpageButtons)}
          isSubpage={!!props.subpageButtons}
          isSelectable={isCompressed}
          isGdprAlert={props.isGdprAlert}
        >
          {renderGraphHeader()}
        </Stopped>
      ) : (
        <Driving
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          loadRide={handleDataAfterFinishedRide}
          handleEndRideDateAndAddress={handleEndRideDateAndAddress}
          isDetail={!!(params || props.subpageButtons)}
        >
          {renderGraphHeader()}
        </Driving>
      );
    }
    return null;
  };

  const renderGraphForOnlinePanel = () => {
    if (!origStatus) {
      return <div />;
    }
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return origStatus === 'STOP' || rideId ? (
        <Stopped
          responseData={responseData}
          isCompressed={isCompressed}
          rideId={rideId}
          vehicleId={vehicle.id}
          loadData={handleDataFromNewRide}
          isDetail={!!(params || props.subpageButtons)}
          isSubpage={!!props.subpageButtons}
          isSelectable={isCompressed && !!props.subpageButtons}
        />
      ) : (
        <Driving
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          loadRide={handleDataAfterFinishedRide}
          handleEndRideDateAndAddress={handleEndRideDateAndAddress}
          isDetail={!!(params || props.subpageButtons)}
        />
      );
    }
    return null;
  };

  return props.subpageButtons && vehicle ? (
    <CmdSubpage
      id="ecodrive-form"
      title={f({ id: 'tables.tripsOverview' })}
      subtitle={`${getSubpageVehicleName(vehicle)} (${getRideStartDate(
        responseData
      )} - ${getRideEndDate(responseData)}) / ${f({
        id: 'tripsOverview.title.ecoScore',
      })}`}
      buttons={props.subpageButtons}
    >
      {renderGraph()}
    </CmdSubpage>
  ) : (
    <div
      className="container-fluid"
      data-cy="ecodrive-graph"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {showSelectRidesModal && vehicle && (
        <SelectRidesModal
          vehicleId={String(vehicle.id)}
          closeCallback={handleModal}
          handleRide={handleRide}
          defaultRideId={rideId || undefined}
          defaultDates={defaultDates}
        />
      )}
      {params && vehicle ? (
        <div>
          <div className="row section page-head" data-cy="reports-head">
            <h4 data-cy="reports-title">
              {`
                 ${f({
                   id: 'rides.ecoScore',
                 })}`}{' '}
              <span className="text-gray-light">{getVehicleName(vehicle)}</span>
            </h4>
          </div>

          {renderGraph()}
        </div>
      ) : (
        <div>
          {renderCompressedGraphAlert()}
          {renderGraphForOnlinePanel()}
        </div>
      )}
    </div>
  );
}
